/**
 * lib/copy-sanitize.ts - shared sanitizer for pipeline-authored sound copy.
 *
 * PRODUCT.md + docs/ANTI_SLOP.md ban em dashes in user-facing copy. The
 * scripts/qa/anti-slop-check.mjs em-dash-in-copy rule only scans
 * app/components/content/lib TSX/JSX/MDX source: it never sees runtime data
 * the weekly sound pipeline writes to the sounds table (title/description),
 * so LLM-generated or scraped copy can ship with an em dash with nothing to
 * catch it. This module is the code-level guarantee that closes that gap:
 * every pipeline/script write path for a sound title or description must run
 * the value through sanitizeSoundCopy() before insert/update.
 *
 * Uses \u2014-style escapes (not literal em-dash/curly-quote characters)
 * throughout so this file itself never trips the checker's own
 * em-dash-in-copy rule, following the same convention already used in
 * lib/schema.ts and app/sounds/[id]/page.tsx.
 */

// Exported so QA/audit scripts (e.g. scripts/quarantine-undescribed-sounds.ts)
// can detect any em dash that slips past the sanitizer, without duplicating
// the escape.
export const EM_DASH = '\u2014'
const CURLY_SINGLE_QUOTES = /[\u2018\u2019]/g
const CURLY_DOUBLE_QUOTES = /[\u201C\u201D]/g

/**
 * Sanitize a sound title or description before it is written to the DB.
 * - spaced em dash becomes ": "
 * - any remaining bare em dash becomes ", "
 * - curly/smart quotes become straight quotes
 * - collapses runs of spaces created by the above (or already present)
 *
 * Intentionally narrow in scope: this guarantees no em dashes reach the DB.
 * It does not rewrite banned marketing words or otherwise editorialize;
 * that stays a human/anti-slop-check concern for hand-authored copy.
 */
export function sanitizeSoundCopy(text: string): string
export function sanitizeSoundCopy(text: string | null | undefined): string | null | undefined
export function sanitizeSoundCopy(text: string | null | undefined): string | null | undefined {
  if (text === null || text === undefined) return text
  if (text === '') return text

  let out = text
    .replace(new RegExp(`\\s+${EM_DASH}\\s+`, 'g'), ': ')
    .replace(new RegExp(EM_DASH, 'g'), ', ')
    .replace(CURLY_SINGLE_QUOTES, "'")
    .replace(CURLY_DOUBLE_QUOTES, '"')

  out = out.replace(/ {2,}/g, ' ').trim()

  return out
}
